'use client'

import { useEffect, useRef, type ReactNode } from 'react'
import { motion, AnimatePresence } from 'framer-motion'

interface DialogProps {
  isOpen: boolean
  onClose: () => void
  /**
   * The element id of the dialog title - wired to `aria-labelledby` so
   * screen readers announce the dialog purpose. Caller is responsible for
   * rendering the title element with this id.
   */
  titleId: string
  /**
   * Suppresses backdrop click + escape close while a request is in flight,
   * so users don't dismiss a dialog mid-submit.
   */
  busy?: boolean
  /** Tailwind max-width class. Defaults to `max-w-md`. */
  maxWidth?: string
  children: ReactNode
}

/**
 * Headless dialog shell - owns chrome (backdrop, card, scale-in animation),
 * focus trap, and escape handling. Callers supply their own title, body,
 * and footer via `children`.
 *
 * Extracted as part of audit finding #2 (docs/ux-audit-2026-05-08.md) so
 * fork-modal.tsx, fork-dialog.tsx, and the dozen-or-so ad-hoc modals don't
 * each reinvent the same accessibility plumbing.
 */
export function Dialog({
  isOpen,
  onClose,
  titleId,
  busy = false,
  maxWidth = 'max-w-md',
  children,
}: DialogProps) {
  const dialogRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!isOpen) return
    const onEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose()
    }
    document.addEventListener('keydown', onEscape)
    return () => document.removeEventListener('keydown', onEscape)
  }, [isOpen, busy, onClose])

  // Tab/Shift-Tab cycle within the dialog so focus can't escape behind the
  // backdrop. Tracks the currently-focused element instead of caching the
  // list so dynamically-added inputs (e.g. conditional fields) participate.
  useEffect(() => {
    if (!isOpen || !dialogRef.current) return
    const dialog = dialogRef.current
    const selector =
      'button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])'

    const onTab = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return
      const focusable = dialog.querySelectorAll<HTMLElement>(selector)
      if (focusable.length === 0) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', onTab)
    return () => document.removeEventListener('keydown', onTab)
  }, [isOpen])

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/60"
            onClick={!busy ? onClose : undefined}
            aria-hidden="true"
          />
          <motion.div
            ref={dialogRef}
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            transition={{ duration: 0.2 }}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            className={`relative z-10 w-full ${maxWidth} rounded-[var(--radius-xl)] border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] p-6 shadow-lg`}
          >
            {children}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  )
}
